import { pageSchema } from "@/helpers/types";
import { z } from "zod";

// DBT Column Tag Schema
export const DbtColumnTagSchema = z.object({
  name: z.string(),
  value: z.string().optional(),
});

export type DbtColumnTag = z.infer<typeof DbtColumnTagSchema>;

// DBT Column Schema
export const DbtColumnSchema = z.object({
  rk: z.string(),
  name: z.string(),
  datatype: z.string(),
  description: z.string().nullish(),
  tags: z
    .array(z.union([DbtColumnTagSchema, z.string()]))
    .optional()
    .default([]),
});

export type DbtColumn = z.infer<typeof DbtColumnSchema>;

const DbtInsightSchema = z.object({
  name: z.string(),
  type: z.string().optional(),
  severity: z.string().optional(),
  insight_metadata: z.unknown().optional(),
});

export type DbtInsight = z.infer<typeof DbtInsightSchema>;

// DBT Model Schema
export const DbtModelSchema = z.object({
  rk: z.string(),
  model_name: z.string(),
  database: z.string(),
  model_schema: z.string(),
  alias: z.string().nullable(),
  project_name: z.string(),
  project_rk: z.string().optional(),
  unique_id: z.string().optional(),
  resource_type: z.string(),
  materialization: z.string().nullish(),
  file_path: z.string().optional(),
  raw_code: z.string().nullable().optional(),
  compiled_code: z.string().nullable().optional(),
  columns: z.array(DbtColumnSchema),
  description: z.string().nullable().optional(),
  tags: z.array(DbtColumnTagSchema).optional().default([]),
  is_stale: z.boolean().optional(),
  last_seen: z.string().nullable().optional(),
  dbt_core_integration_id: z.number().optional(),
  dbt_core_integration_environment_id: z.number().optional(),
  is_deleted: z.boolean().optional(),
  deleted_at: z.string().nullable().optional(),
  insights: z.array(DbtInsightSchema).optional(),
  severity: z.string().optional(),
});

export type DbtModel = z.infer<typeof DbtModelSchema>;

// DBT Models Response Schema (paginated)
export const DbtModelsResponseSchema = pageSchema(DbtModelSchema);

export type DbtModelsResponse = z.infer<typeof DbtModelsResponseSchema>;

// Filter Option Schema
export const FilterOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  count: z.number().optional(),
});

export type FilterOption = z.infer<typeof FilterOptionSchema>;

// Integration Filter Option Schema (with integration_type and connection_name)
export const IntegrationFilterOptionSchema = z.object({
  label: z.string(),
  value: z.coerce.string(),
  count: z.number().optional(),
  integration_type: z.enum(["dbt_core", "dbt_cloud"]),
  connection_name: z.string().nullable().optional(),
});

export type IntegrationFilterOption = z.infer<typeof IntegrationFilterOptionSchema>;

// Integration With Environments Schema (for cascade filter: environment options grouped by integration)
export const IntegrationWithEnvironmentsSchema = z.object({
  integration_value: z.string(),
  environments: z.array(FilterOptionSchema),
});

export type IntegrationWithEnvironments = z.infer<typeof IntegrationWithEnvironmentsSchema>;

// DBT Model Filters Response Schema
export const DbtModelFiltersResponseSchema = z.object({
  dbt_integrations: z.array(IntegrationFilterOptionSchema),
  environments: z.array(FilterOptionSchema),
  projects: z.array(FilterOptionSchema),
  resource_types: z.array(FilterOptionSchema),
  materializations: z.array(FilterOptionSchema),
  databases: z.array(FilterOptionSchema).optional().default([]),
  schemas: z.array(FilterOptionSchema).optional().default([]),
  is_stale: z.array(FilterOptionSchema).optional(),
  integrations_with_environments: z.array(IntegrationWithEnvironmentsSchema).optional().default([]),
});

export type DbtModelFiltersResponse = z.infer<typeof DbtModelFiltersResponseSchema>;

// DBT Model By RK Request Schema
export const DbtModelByRkRequestSchema = z.object({
  dbt_model_rk: z.string(),
});

export type DbtModelByRkRequest = z.infer<typeof DbtModelByRkRequestSchema>;

// DBT Model By RK Response Schema
export const DbtModelByRkResponseSchema = z.object({
  project_name: z.string(),
  dbt_core_integration_id: z.number(),
  dbt_core_integration_environment_id: z.number(),
  rk: z.string(),
  project_rk: z.string(),
  model_name: z.string(),
  unique_id: z.string(),
  resource_type: z.string(),
  alias: z.string().nullable(),
  description: z.string().nullable(),
  materialization: z.string().nullable(),
  database: z.string(),
  model_schema: z.string(),
  file_path: z.string(),
  raw_code: z.string().nullable(),
  compiled_code: z.string().nullable(),
  columns: z.array(DbtColumnSchema),
  is_stale: z.boolean(),
  last_seen: z.string().nullable(),
  insights: z.array(z.unknown()), // Define specific schema when insight structure is known
});

export type DbtModelByRkResponse = z.infer<typeof DbtModelByRkResponseSchema>;

// Get DBT Models Request Parameters
export interface GetDbtModelsParams {
  page?: number;
  size?: number;
  search?: string;
  dbt_core_integration_ids?: string[];
  dbt_core_integration_environment_ids?: string[];
  project_rk?: string[];
  resource_type?: string[];
  materialization?: string[];
  database?: string[];
  model_schema?: string[];
  is_stale?: string[];
}
